/**
 * Tačne decimale — ceo broj u najmanjoj jedinici, uz `scale` zapisan UZ KOLONU.
 *
 * Blizanac je `apps/backend/blockchain/utils/decimalScale.js`; dva su jer
 * backend i klijent nemaju zajednički paket. Ako se jedan menja, mora i drugi.
 *
 * `DECIMAL` na izvoru postoji baš zato što novac ne trpi binarni pokretni
 * zarez. Preslikavanje u `double` ga je kvarilo tiho: izmereno na
 * `cashpago1.venda.valor`, 381 od 400 vrednosti nije bilo tačno na dve
 * decimale, a `7978.68` je na lancu stajalo kao `7978.68000000000029104`.
 *
 * Rešenje je ono koje je i MySQL izabrao za sopstveni zapis, i koje je MongoDB
 * preporučivao dok BSON nije dobio `Decimal128`: čuvaj cifre kao ceo broj, a
 * položaj zareza drži jednom, u shemi. `7978.68` sa `scale = 2` je `797868`.
 *
 * Zato ovde nema nijedne operacije u pokretnom zarezu — sve je pomeranje
 * zareza nad stringom.
 */

export const DECIMAL_RE = /^([+-]?)(\d*)(?:\.(\d*))?$/;

/**
 * Zapis sa eksponentom — `1E+6000`, `1e-3`, `-2.5E3`.
 *
 * Ne prihvata se, ali se PREPOZNAJE, jer to jeste broj. Do 28.08.2026. je
 * dobijao istu poruku kao `abc` — „is not a decimal number" — pa je korisniku
 * govorilo da je pogrešio tip vrednosti, umesto da je pogrešio zapis.
 *
 * Blizanac je `apps/backend/blockchain/utils/decimalScale.js`.
 */
export const EXPONENT_RE = /^[+-]?(?:\d+\.?\d*|\.\d+)[eE][+-]?\d+$/;

/**
 * Decimalni zapis → ceo broj pomnožen sa 10^scale, kao string.
 *
 * @throws kad vrednost ima više decimala nego što kolona nosi. Tiho
 *         zaokruživanje bi značilo da na lancu stoji iznos koji izvor nije
 *         imao — isto načelo po kom `Decimal128` odbija 35. cifru.
 */
export const toScaledInteger = (value, scale) => {
  const s = String(value ?? "").trim();
  if (s === "") return "0";

  const m = DECIMAL_RE.exec(s);
  if (!m || (!m[2] && !m[3])) {
    if (EXPONENT_RE.test(s)) {
      throw new Error(
        `Value ${JSON.stringify(s)} is written in exponent notation, which this ` +
        `column cannot store; write it out in full, as ${scale} decimal ` +
        `place${scale === 1 ? "" : "s"}`,
      );
    }
    throw new Error(`Value ${JSON.stringify(s)} is not a decimal number`);
  }

  const sign = m[1] === "-" ? "-" : "";
  const int = m[2] || "0";
  const frac = m[3] || "";

  if (frac.length > scale) {
    // Dozvoljeno je samo kad su suvišne cifre nule — `1.500` u koloni sa dve
    // decimale je ista vrednost, `1.505` nije.
    if (!/^0*$/.test(frac.slice(scale))) {
      throw new Error(
        `Value ${JSON.stringify(s)} has ${frac.length} decimals, column holds ${scale}`,
      );
    }
  }

  const digits = int + frac.slice(0, scale).padEnd(scale, "0");
  // `BigInt` sređuje vodeće nule i `-0`.
  return BigInt(sign + (digits || "0")).toString();
};

/**
 * Ceo broj → decimalni zapis sa `scale` decimala. Obrnuto od gornjeg, isto bez
 * pokretnog zareza.
 */
export const fromScaledInteger = (value, scale) => {
  const s = String(value ?? "").trim();
  if (s === "") return "";
  if (!/^[+-]?\d+$/.test(s)) return s;

  const neg = s.startsWith("-");
  const digits = s.replace(/^[+-]/, "").padStart(scale + 1, "0");
  const out = scale > 0
    ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}`
    : digits;
  return (neg && /[1-9]/.test(digits) ? "-" : "") + out;
};

/**
 * Najveći broj cifara koji `int64_t` sigurno nosi. `DECIMAL(p,s)` skaliran ima
 * tačno `p` cifara, pa je `p <= 18` uvek bezbedno (`int64` ide do 19 cifara,
 * ali ne do svake 19-cifrene vrednosti).
 *
 * Ovo je činjenica o TIPU. Koliko cifara kolona SME da traži izvodi se iz nje
 * (`MAX_DECIMAL_PRECISION` u `fieldTypes.js`) — da ista osamnaestica ne stoji
 * na dva mesta i razmimoiđe se kad `uint128` prohoda i granica postane 38.
 *
 * Broj DECIMALA nema svoju granicu: omeđen je samo pravilom `scale <= precision`.
 */
export const MAX_INT64_DIGITS = 18;

/**
 * Koliko cifara pouzdano nosi `int128_t`, uzeto konzervativno.
 *
 * Postoji od 26.08.2026, kad je `int128` prohodao kroz generator — do tada je
 * decimala mogla biti samo `int64`, pa je 18 bilo i granica tipa i granica
 * kolone. Mongo `Decimal128` nosi 34 cifre i bez ovoga se ne može uvesti tačno.
 */
export const MAX_INT128_DIGITS = 38;

